// Token types for equation editing

import type { ConstraintFilterCondition } from '@/types/api/constraints'

export interface TermToken {
  type: 'term'
  id: string
  field: string
  factor?: number
  filter?: Record<string, ConstraintFilterCondition>
}

export interface UnresolvedTextToken {
  type: 'unresolved_text'
  id: string
  text: string
}

// Extract the first in_filter_list ID from any filter record,
// regardless of which field key it uses (isin, cusip, ticker, etc.).
export function extractFilterListId(
  filter?: Record<string, ConstraintFilterCondition>,
): string | undefined {
  if (!filter) return undefined
  for (const condition of Object.values(filter)) {
    if (condition?.in_filter_list) return condition.in_filter_list
  }
  return undefined
}

export type EquationToken =
  | TermToken
  | UnresolvedTextToken
  | { type: 'operator'; op: '+' | '-' | '*' | '/' }
  | { type: 'paren'; side: 'open' | 'close' }

export type Operator = '+' | '-' | '*' | '/'

const OPERATOR_PRECEDENCE: Record<Operator, number> = {
  '+': 1,
  '-': 1,
  '*': 2,
  '/': 2,
}

export function getOperatorPrecedence(op: Operator): number {
  return OPERATOR_PRECEDENCE[op]
}

export function insertTokenAt(
  tokens: EquationToken[],
  index: number,
  token: EquationToken,
): EquationToken[] {
  const result = [...tokens]
  result.splice(index, 0, token)
  return result
}

export function removeTokenAt(
  tokens: EquationToken[],
  index: number,
): EquationToken[] {
  const result = [...tokens]
  result.splice(index, 1)
  return result
}

// Remove a term by id and clean up the orphaned adjacent operator:
//   [A + B] → remove B → [A]  (cleans up the + before)
//   [A + B] → remove A → [B]  (cleans up the + after)
export function removeTermById(
  tokens: EquationToken[],
  termId: string,
): EquationToken[] | null {
  const tokenIndex = tokens.findIndex(
    (t) => t.type === 'term' && t.id === termId,
  )
  if (tokenIndex === -1) return null

  let result = removeTokenAt(tokens, tokenIndex)

  const beforeIdx = tokenIndex - 1
  const afterIdx = tokenIndex

  if (beforeIdx >= 0 && result[beforeIdx]?.type === 'operator') {
    result = removeTokenAt(result, beforeIdx)
  } else if (
    afterIdx < result.length &&
    result[afterIdx]?.type === 'operator'
  ) {
    result = removeTokenAt(result, afterIdx)
  }

  return result
}

export function generateDefaultTokens(
  terms: Array<{
    id: string
    field: string
    factor?: number
    filter?: Record<string, ConstraintFilterCondition>
  }>,
): EquationToken[] {
  if (terms.length === 0) return []
  const tokens: EquationToken[] = [
    {
      type: 'term',
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      id: terms[0]!.id,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      field: terms[0]!.field,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      factor: terms[0]!.factor,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      filter: terms[0]!.filter,
    },
  ]
  for (let i = 1; i < terms.length; i++) {
    tokens.push({ type: 'operator', op: '+' })
    tokens.push({
      type: 'term',
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      id: terms[i]!.id,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      field: terms[i]!.field,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      factor: terms[i]!.factor,
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      filter: terms[i]!.filter,
    })
  }
  return tokens
}

interface ValidationResult {
  valid: boolean
  errors: string[]
}

function hasUnresolvedTokens(tokens: EquationToken[]): boolean {
  return tokens.some((t) => t.type === 'unresolved_text')
}

export function validateTokens(tokens: EquationToken[]): ValidationResult {
  const errors: string[] = []

  if (tokens.length === 0) {
    return { valid: true, errors: [] }
  }

  if (hasUnresolvedTokens(tokens)) {
    errors.push('Resolve or remove all unresolved text tokens before saving')
  }

  // Check matched parentheses
  let parenDepth = 0
  for (const token of tokens) {
    if (token.type === 'paren') {
      if (token.side === 'open') parenDepth++
      else parenDepth--
      if (parenDepth < 0) {
        errors.push('Unmatched closing parenthesis')
        break
      }
    }
  }
  if (parenDepth > 0) {
    errors.push('Unmatched opening parenthesis')
  }

  // Check for empty parens
  for (let i = 0; i < tokens.length - 1; i++) {
    const curr = tokens[i]
    const next = tokens[i + 1]
    if (
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      curr!.type === 'paren' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      curr!.side === 'open' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      next!.type === 'paren' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      next!.side === 'close'
    ) {
      errors.push('Empty parentheses')
    }
  }

  // Check for consecutive operators
  for (let i = 0; i < tokens.length - 1; i++) {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    if (tokens[i]!.type === 'operator' && tokens[i + 1]!.type === 'operator') {
      errors.push('Consecutive operators')
      break
    }
  }

  // Check for consecutive terms (no operator between them)
  const isTermLike = (t: EquationToken) =>
    t.type === 'term' || t.type === 'unresolved_text'
  for (let i = 0; i < tokens.length - 1; i++) {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    if (isTermLike(tokens[i]!) && isTermLike(tokens[i + 1]!)) {
      errors.push('Missing operator between terms')
      break
    }
  }

  // Check for operator after open paren or before close paren
  for (let i = 0; i < tokens.length - 1; i++) {
    const curr = tokens[i]
    const next = tokens[i + 1]
    if (
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      curr!.type === 'paren' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      curr!.side === 'open' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      next!.type === 'operator'
    ) {
      errors.push('Operator cannot follow an opening parenthesis')
      break
    }
    if (
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      curr!.type === 'operator' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      next!.type === 'paren' &&
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      next!.side === 'close'
    ) {
      errors.push('Operator cannot precede a closing parenthesis')
      break
    }
  }

  // Check expression starts with a term or opening paren
  const first = tokens[0]
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  if (first!.type === 'operator') {
    errors.push('Expression cannot start with an operator')
  }
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  if (first!.type === 'paren' && first!.side === 'close') {
    errors.push('Expression cannot start with a closing parenthesis')
  }

  // Check expression ends with a term or closing paren
  const last = tokens[tokens.length - 1]
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  if (last!.type === 'operator') {
    errors.push('Expression cannot end with an operator')
  }
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  if (last!.type === 'paren' && last!.side === 'open') {
    errors.push('Expression cannot end with an opening parenthesis')
  }

  return { valid: errors.length === 0, errors }
}
